import { ComponentProps } from 'react'

import { parseWithZod } from '@conform-to/zod'
import {
  ActionFunctionArgs,
  json,
  LoaderFunction,
  MetaFunction,
  redirect,
} from '@remix-run/node'
import { useLoaderData, useSearchParams } from '@remix-run/react'
import invariant from 'tiny-invariant'
import { z } from 'zod'

import { Breadcrumb, Container } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import { PREFECTURES } from '~/domains'
import {
  isSearchResultOrderType,
  partialNumberOfUsersMultiSelectFormSchema,
  partialFacilitiesSchemaSchema,
  PrefectureHeader,
  SEARCH_RESULT_ORDERS,
  SearchResultOrder,
  SearchResultPage,
  searchFormSchema,
  partialMonthlyCostsFormSchema,
} from '~/features'
import {
  partialAreasMultiSelectFormSchema,
  partialKeywordsSchema,
  partialOfficeTypeMultiSelectFormSchema,
  TopFooter,
} from '~/features/common'
import {
  getAllFacilities,
  getAllTypes,
  searchAreas,
  searchPrefecture,
} from '~/models/master.server'
import { searchOffice, SearchOfficeConditions } from '~/models/office.server'
import {
  Area,
  Facility,
  Office,
  Prefecture,
  Transportation,
  Type,
} from '~/models/types'

export const searchQuerySchema = z
  .object({
    page: z.string().optional(),
    sortOrder: z.string().optional(),
    maxPeople: z.coerce.number().int().min(1).optional(),
  })
  .merge(partialKeywordsSchema)
  .merge(partialAreasMultiSelectFormSchema)
  .merge(partialOfficeTypeMultiSelectFormSchema)
  .merge(partialNumberOfUsersMultiSelectFormSchema)
  .merge(partialFacilitiesSchemaSchema)
  .merge(partialMonthlyCostsFormSchema)

const isValidPrefectureSlug = (slug: string) => {
  return PREFECTURES.some((pref) => pref.slug === slug)
}

const getPrefectureData = async (
  slug: string,
): Promise<{
  prefecture: Prefecture
} | null> => {
  const [prefecture] = await searchPrefecture({ slug })
  if (!prefecture) return null

  return { prefecture }
}

const parseParams = (searchParams: URLSearchParams) => {
  const multiSelectKeys = [
    ...Object.keys(partialAreasMultiSelectFormSchema.shape),
    ...Object.keys(partialOfficeTypeMultiSelectFormSchema.shape),
    ...Object.keys(partialNumberOfUsersMultiSelectFormSchema.shape),
    ...Object.keys(partialFacilitiesSchemaSchema.shape),
  ]

  const transformedParams = Object.fromEntries(
    Array.from(searchParams.entries()).map(([key, value]) => {
      if (multiSelectKeys.includes(key)) {
        return [key, value.includes(',') ? value.split(',') : [value]]
      }

      return [key, value]
    }),
  )

  const parsedSearchParams = searchQuerySchema.safeParse(transformedParams)

  return { parsedSearchParams }
}

const DISPLAY_RESULT_COUNT = 30
const createPageCondition = (
  page: z.infer<typeof searchQuerySchema>['page'],
) => {
  return {
    skip: page ? (Number(page) - 1) * DISPLAY_RESULT_COUNT : 0,
    take: DISPLAY_RESULT_COUNT,
  }
}

const createSortOrderCondition = (
  sortOrder: z.infer<typeof searchQuerySchema>['sortOrder'],
): SearchOfficeConditions['sortOrder'] => {
  switch (sortOrder) {
    case 'registerDateAsc':
      return 'NEWEST'
    case 'monthlyRentAsc':
      return 'CHEAPEST'
    case 'sizeDesc':
      return 'LARGEST'
    case 'updateDateDesc':
      return 'RECENTLY_UPDATED'
    default:
      return undefined
  }
}

const getSearchResults = async (
  searchParams: z.infer<typeof searchQuerySchema>,
  prefectureSlug: string,
) => {
  const searchConditions: SearchOfficeConditions = {
    areaIds: searchParams.areas?.map(Number) ?? [],
    typeIds: searchParams.officeTypes?.map(Number) ?? [],
    facilityIds: searchParams.facilities?.map(Number) ?? [],
    minPeople: Number(searchParams.minPeople) || undefined,
    maxPeople: Number(searchParams.maxPeople) || undefined,
    maxMonthlyRent: Number(searchParams.maxMonthlyRent) || undefined,
    minMonthlyRent: Number(searchParams.minMonthlyRent) || undefined,
    freeWord: searchParams.keyword,
    prefectureSlug,

    // ページング、ソート条件
    skip: createPageCondition(searchParams.page).skip,
    take: createPageCondition(searchParams.page).take,
    sortOrder: createSortOrderCondition(searchParams.sortOrder),
  }

  // nullとundefinedはパラメータに含めない
  const filteredParams = Object.fromEntries(
    Object.entries(searchConditions).filter(
      (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [key, value],
      ) => value != null,
    ),
  )

  return await searchOffice(filteredParams)
}

const PAGE_NUMBER_PARAM_NAME = 'page'
const DEFAULT_PAGE = 1
const SORT_ORDER_PARAM_NAME = 'sortOrder'
const useParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const pageParam =
    Number(searchParams.get(PAGE_NUMBER_PARAM_NAME)) || DEFAULT_PAGE
  const changePage = (page: number) => {
    setSearchParams((prev) => {
      prev.set(PAGE_NUMBER_PARAM_NAME, page.toString())
      return prev
    })
  }

  const sortOrderParamString = searchParams.get(SORT_ORDER_PARAM_NAME)
  const sortOrderParam = isSearchResultOrderType(sortOrderParamString)
    ? sortOrderParamString
    : SEARCH_RESULT_ORDERS[0].value

  const changeSortOrder = (sortOrder: SearchResultOrder) => {
    setSearchParams((prev) => {
      prev.set(SORT_ORDER_PARAM_NAME, sortOrder)
      prev.set(PAGE_NUMBER_PARAM_NAME, DEFAULT_PAGE.toString())
      return prev
    })
  }

  return {
    pageParam,
    changePage,
    sortOrderParam,
    changeSortOrder,
  }
}

const convertTransportationToText = (transportation: Transportation) => {
  switch (transportation) {
    case 'WALK':
      return '徒歩'
    case 'BUS':
      return 'バス'
    default:
      return ''
  }
}

const createTransportationInfo = (stations: Office['stations']) => {
  if (!stations) return ''
  return stations
    .map((station) => {
      return `${station.name}駅${convertTransportationToText(station.transportation)}${station.time}分`
    })
    .join('、')
}

const createOtherAccessInfo = (otherAccesses: Office['otherAccesses']) => {
  if (!otherAccesses) return ''
  return otherAccesses
    .map((otherAccess) => {
      return `（${otherAccess.accessType === 'BUS' ? 'バス' : 'その他'}）${otherAccess.description}`
    })
    .join('、')
}

const transformOfficesForComponent = (
  offices: Office[],
  prefectureSlug: string,
): ComponentProps<typeof SearchResultPage>['offices'] => {
  return offices.map((office) => ({
    id: office.id,
    facilityName: office.name,
    address: office.address,
    buildingName: office.building || '',
    transportationInfo: createTransportationInfo(office.stations),
    otherAccess: createOtherAccessInfo(office.otherAccesses),
    officeDetailLink: `/prefectures/${prefectureSlug}/offices/${office.id}`,
    contactLink: `/prefectures/${prefectureSlug}/offices/${office.id}/contact`,
    imageSrc:
      (
        office.images
          .sort((a, b) => a.priority - b.priority)
          .find((image) => image.isEyeCatch) || office.images[0]
      )?.url || NO_IMAGE_URL,
    spaces: office.spaces
      .filter((space) => space.availability !== 'UNAVAILABLE')
      .map((space) => ({
        id: space.id,
        types: space.type.map((t) => t.name),
        price: space.monthlyCostExcludingTax,
        priceTaxIncluded: space.monthlyCostIncludingTax,
        pricePrivateFlag: space.isMonthlyCostPrivate,
        capacity: space.maxPeople,
      })),
  }))
}

const createMaxPeopleConditionText = (maxPeopleCondition: {
  min?: number
  max?: number
}) => {
  const { min, max } = maxPeopleCondition

  if (!min && !max) return ''
  if (min && !max) return `${min}名以上`
  if (!min && max) return `${max}名以下`
  return `${min}名〜${max}名`
}

const createMonthlyRentConditionText = (monthlyRentCondition: {
  min?: number
  max?: number
}) => {
  const { min, max } = monthlyRentCondition

  if (!min && !max) return ''
  if (min && !max) return `${min}円〜`
  if (!min && max) return `〜${max}円`
  return `${min}円〜${max}円`
}

const createSearchConditionText = (
  searchParams: z.infer<typeof searchQuerySchema>,
  allAreas: Area[],
  allTypes: Type[],
  allFacilities: Facility[],
) => {
  const areaNamesText = allAreas
    .filter((area) => searchParams.areas?.includes(area.id.toString()))
    .map((area) => area.name)
    .join('、')

  const typeNamesText = allTypes
    .filter((type) => searchParams.officeTypes?.includes(type.id.toString()))
    .map((type) => type.name)
    .join('、')

  const facilityNamesText = allFacilities
    .filter((facility) =>
      searchParams.facilities?.includes(facility.id.toString()),
    )
    .map((facility) => facility.name)
    .join('、')

  const maxPeopleText = createMaxPeopleConditionText({
    min: searchParams.minPeople,
    max: searchParams.maxPeople,
  })

  const monthlyRentText = createMonthlyRentConditionText({
    min: searchParams.minMonthlyRent,
    max: searchParams.maxMonthlyRent,
  })

  const searchConditionText =
    [
      areaNamesText,
      typeNamesText,
      facilityNamesText,
      maxPeopleText,
      monthlyRentText,
    ]
      .filter(Boolean)
      .join('、') || '指定なし'

  return { searchConditionText }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  const submission = parseWithZod(formData, { schema: searchFormSchema })

  const prefectureSlug = formData.get('prefectureSlug')

  if (!prefectureSlug) {
    return submission.reply({
      formErrors: ['prefectureSlug is not defined'],
    })
  }

  if (submission.status !== 'success') {
    return submission.reply()
  }

  const queryParams = new URLSearchParams()

  formData.forEach((value, key) => {
    if (key !== 'prefectureSlug' && value.toString().trim() !== '') {
      if (queryParams.has(key)) {
        const existingValue = queryParams.get(key)
        queryParams.set(key, `${existingValue},${value.toString()}`)
      } else {
        queryParams.set(key, value.toString())
      }
    }
  })

  const searchUrl = `/prefectures/${prefectureSlug}/search?${queryParams.toString()}`

  return redirect(searchUrl)
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const { searchParams } = new URL(request.url)

  const { parsedSearchParams } = parseParams(searchParams)

  if (!parsedSearchParams.success) {
    throw new Response('Invalid search parameters', { status: 400 })
  }

  const { prefectureSlug } = params
  invariant(prefectureSlug, 'prefectureSlug not found')

  const prefectureData = await getPrefectureData(prefectureSlug)

  if (!prefectureData || !isValidPrefectureSlug(prefectureSlug))
    throw new Response('Not Found', { status: 404, statusText: 'Not Found.' })

  const [areasInPrefecture, allTypes, allFacilities] = await Promise.all([
    searchAreas({ prefectureId: prefectureData.prefecture.id }),
    getAllTypes(),
    getAllFacilities(),
  ])

  const { searchConditionText } = createSearchConditionText(
    parsedSearchParams.data,
    areasInPrefecture,
    allTypes,
    allFacilities,
  )

  const { offices, count } = await getSearchResults(
    parsedSearchParams.data,
    prefectureData.prefecture.slug,
  )

  const parsedParamsData = parsedSearchParams.data

  const initialFormValues = {
    areas: parsedParamsData.areas || [],
    officeTypes: parsedParamsData.officeTypes || [],
    facilities: parsedParamsData.facilities || [],
    minPeople: parsedParamsData.minPeople?.toString() || '',
    minMonthlyRent: parsedParamsData.minMonthlyRent?.toString() || '',
    maxMonthlyRent: parsedParamsData.maxMonthlyRent?.toString() || '',
    keyword: parsedParamsData.keyword || '',
  }

  return json({
    prefecture: prefectureData.prefecture,
    searchResultCount: count,
    offices,
    searchConditionText,
    areasInPrefecture,
    allTypes,
    allFacilities,
    initialFormValues,
    prefectureSlug,
  })
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const prefectureName = data?.prefecture.name || ''
  const titlePrefix = prefectureName
    ? `${prefectureName}での検索結果`
    : '検索結果'
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    {
      title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト',
    },
  ]
}

export default function Index() {
  const {
    prefecture,
    searchResultCount,
    offices,
    searchConditionText,
    areasInPrefecture,
    allTypes,
    allFacilities,
    initialFormValues,
    prefectureSlug,
  } = useLoaderData<typeof loader>()

  const { pageParam, changePage, sortOrderParam, changeSortOrder } = useParams()

  return (
    <div>
      <PrefectureHeader isWide prefecture={prefecture} />
      <Container>
        <Breadcrumb
          links={[
            { text: 'トップ', href: '/' },
            { text: prefecture.name, href: `/prefectures/${prefecture.slug}` },
            {
              text: `シェアオフィス・コワーキングスペース一覧`,
            },
          ]}
        />
      </Container>
      <SearchResultPage
        areas={areasInPrefecture}
        changePage={changePage}
        changeSortOrder={changeSortOrder}
        currentPage={pageParam}
        facilities={allFacilities}
        initialFormValues={initialFormValues}
        isFromTopPage={false}
        officeTypes={allTypes}
        offices={transformOfficesForComponent(offices, prefecture.slug)}
        prefectureName={prefecture.name}
        prefectureSlug={prefectureSlug}
        searchConditionText={searchConditionText}
        searchResultCount={searchResultCount}
        sortOrder={sortOrderParam}
      />
      <TopFooter />
    </div>
  )
}
